import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { timer } from 'rxjs';
import { DesignState } from '../store/design.reducer';
import { EngineService } from '../containers/draw-area/threejs/threejs.service';
import { PanelArray } from '../models/panel-array.model';
import { fromPanelArrayActions } from '../store/panel-array';
import Util, { meshTypeEnum } from '../containers/draw-area/threejs/helpers/Utility';

/**
 * Service to handle scene-store synchronization with grace periods
 * to prevent aggressive deletion during normal operations
 */
@Injectable({
  providedIn: 'root'
})
export class SceneStoreSyncService implements OnDestroy {
  // Track pending validation subscriptions keyed by panel id
  private pendingValidations = new Map<string, { sub: import('rxjs').Subscription }>();
  private readonly GRACE_PERIOD_MS = Util.SCENE_SYNC_GRACE_PERIOD_MS;
  
  constructor(
    private store: Store<DesignState>,
    private engServ: EngineService
  ) {}

  /**
   * Validates panel array sync with grace period
   * @param panelArray Panel array to validate
   */
  validatePanelArraySync(panelArray: PanelArray): void {
    const validationKey = `panel-${panelArray.id}`;

    // If a pending validation exists, cancel the previous subscription
    const existing = this.pendingValidations.get(validationKey);
    if (existing) {
      existing.sub.unsubscribe();
      this.pendingValidations.delete(validationKey);
    }

    // Use RxJS timer for better testability & Angular zone awareness
    const sub = timer(this.GRACE_PERIOD_MS).subscribe(() => {
      this.performDelayedValidation(panelArray);
      this.pendingValidations.delete(validationKey);
    });

    this.pendingValidations.set(validationKey, { sub });
  }

  /**
   * Cancels pending validation for a panel array
   * @param panelArrayId Panel array ID to cancel validation for
   */
  cancelPendingValidation(panelArrayId: string): void {
    const validationKey = `panel-${panelArrayId}`;
    const existing = this.pendingValidations.get(validationKey);
    if (existing) {
      existing.sub.unsubscribe();
      this.pendingValidations.delete(validationKey);
    }
  }

  /**
   * Performs delayed validation after grace period
   * @param panelArray Panel array to validate
   * @private
   */
  private performDelayedValidation(panelArray: PanelArray): void {
    // Check if scene exists and has objects
    if (!this.engServ.scene?.children.length) {
      console.warn(`Scene not available for panel array validation: ${panelArray.id}`);
      return;
    }

    const allPanelArrays = this.engServ.scene?.getObjectsByProperty('name', meshTypeEnum.panelArray);
    const inScene = allPanelArrays?.find(pa => pa.userData.enactStoreId === panelArray.id);
    
    if (!inScene) {
      // Only delete after grace period and verification
      console.warn(`Panel array ${panelArray.id} not found in scene after grace period - removing from store`);
      this.store.dispatch(fromPanelArrayActions.PanelArrayDelete({ panelArray }));
    } else {
      console.log(`Panel array ${panelArray.id} found in scene - keeping in store`);
    }
  }

  /**
   * Immediate validation without grace period (for urgent cases)
   * @param panelArray Panel array to validate immediately
   */
  validateImmediately(panelArray: PanelArray): boolean {
    if (!this.engServ.scene?.children.length) {
      return false;
    }

    const allPanelArrays = this.engServ.scene?.getObjectsByProperty('name', meshTypeEnum.panelArray);
    const inScene = allPanelArrays?.find(pa => pa.userData.enactStoreId === panelArray.id);
    
    return !!inScene;
  }

  /**
   * Clears all pending validations (useful for cleanup)
   */
  clearAllPendingValidations(): void {
    for (const entry of this.pendingValidations.values()) {
      entry.sub.unsubscribe();
    }
    this.pendingValidations.clear();
  }

  /**
   * Gets count of pending validations (useful for testing/debugging)
   */
  getPendingValidationCount(): number {
    return this.pendingValidations.size;
  }

  /**
   * Angular lifecycle cleanup to prevent memory leaks if app persists root service references
   */
  ngOnDestroy(): void {
    this.clearAllPendingValidations();
  }
}